import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonSearchbar,
  IonFab,
  IonFabButton,
  IonModal,
  IonInput,
  IonTextarea,
  IonToast,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonActionSheet,
  IonAlert,
  IonChip,
  IonBadge,
  IonList,
  IonSelect,
  IonSelectOption,
  IonCheckbox,
} from '@ionic/react';
import {
  addOutline,
  businessOutline,
  mailOutline,
  callOutline,
  cashOutline,
  createOutline,
  trashOutline,
  ellipsisVertical,
  walletOutline,
  documentTextOutline,
  peopleOutline,
  warningOutline,
  listOutline,
  cardOutline,
  addCircleOutline,
  checkboxOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { supplierService } from '../services/api';
import { Supplier } from '../types';
import { format } from 'date-fns';
import CenteredLoader from '../components/CenteredLoader';
import { getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currencyConstants';
import { eventBus, EVENTS } from '../utils/eventBus';

interface SupplierWithBalance extends Supplier {
  totalFromSales: number;
  calculatedBalance: number;
  salesCount: number;
}

const Suppliers: React.FC = () => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<SupplierWithBalance[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<SupplierWithBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierWithBalance | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    sortCode: '',
    routingNumber: '',
    iban: '',
    swiftCode: '',
    bankCountry: '',
    accountType: '' as '' | 'private' | 'business',
    paypalEmail: '',
    initialBalance: 0,
  });


  const [transactionHistory, setTransactionHistory] = useState<any>(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  const [paymentHistory, setPaymentHistory] = useState<any>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [unpaidTransactions, setUnpaidTransactions] = useState<any>(null);
  const [loadingUnpaidTransactions, setLoadingUnpaidTransactions] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: 0,
    type: 'sent' as 'sent' | 'received',
    paymentMethod: 'bankTransfer' as 'cash' | 'card' | 'bankTransfer' | 'paypal' | 'other',
    paymentDate: new Date().toISOString().split('T')[0],
    description: '',
    reference: '',
    notes: '',
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    filterSuppliers();
  }, [suppliers, searchText]);

  useEffect(() => {
    // Subscribe to sales events to automatically refresh supplier balances
    const unsubscribeSalesRefresh = eventBus.on(EVENTS.REFRESH_SALES, () => {
      console.log('Suppliers: Received REFRESH_SALES event, refreshing supplier data...');
      loadSuppliers();
    });

    const unsubscribeSaleUpdated = eventBus.on(EVENTS.SALE_UPDATED, () => {
      console.log('Suppliers: Received SALE_UPDATED event, refreshing supplier data...');
      loadSuppliers();
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeSalesRefresh();
      unsubscribeSaleUpdated();
    };
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await supplierService.getAllWithBalance();
      setSuppliers(data || []);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      setToastMessage('Failed to load suppliers');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const filterSuppliers = () => {
    if (!searchText) {
      setFilteredSuppliers(suppliers);
      return;
    }

    const search = searchText.toLowerCase();
    const filtered = suppliers.filter(
      (supplier) =>
        supplier.name.toLowerCase().includes(search) ||
        supplier.company?.toLowerCase().includes(search) ||
        supplier.email?.toLowerCase().includes(search) ||
        supplier.phone?.toLowerCase().includes(search)
    );
    setFilteredSuppliers(filtered);
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadSuppliers();
    event.detail.complete();
  };

  const handleAddSupplier = async () => {
    if (!formData.name.trim()) {
      setToastMessage('Supplier name is required');
      setShowToast(true);
      return;
    }

    try {
      await supplierService.create(formData);
      setToastMessage('Supplier added successfully');
      setShowToast(true);
      setShowAddModal(false);
      resetForm();
      await loadSuppliers();
      // Emit event to notify other components about supplier changes
      eventBus.emit(EVENTS.REFRESH_SALES);
    } catch (error: any) {
      setToastMessage(error.response?.data?.message || 'Failed to add supplier');
      setShowToast(true);
    }
  };

  const handleEditSupplier = async () => {
    if (!selectedSupplier || !formData.name.trim()) return;

    try {
      await supplierService.update(selectedSupplier._id || selectedSupplier.id || '', formData);
      setToastMessage('Supplier updated successfully');
      setShowToast(true);
      setShowEditModal(false);
      resetForm();
      await loadSuppliers();
      // Emit event to notify other components about supplier changes
      eventBus.emit(EVENTS.REFRESH_SALES);
    } catch (error: any) {
      setToastMessage(error.response?.data?.message || 'Failed to update supplier');
      setShowToast(true);
    }
  };

  const handleDeleteSupplier = async () => {
    if (!selectedSupplier) return;

    try {
      await supplierService.delete(selectedSupplier._id || selectedSupplier.id || '');
      setToastMessage('Supplier deleted successfully');
      setShowToast(true);
      setShowDeleteAlert(false);
      setSelectedSupplier(null);
      await loadSuppliers();
      // Emit event to notify other components about supplier changes
      eventBus.emit(EVENTS.REFRESH_SALES);
    } catch (error: any) {
      setToastMessage(error.response?.data?.message || 'Failed to delete supplier');
      setShowToast(true);
    }
  };


  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
      bankName: '',
      accountName: '',
      accountNumber: '',
      sortCode: '',
      routingNumber: '',
      iban: '',
      swiftCode: '',
      bankCountry: '',
      accountType: '',
      paypalEmail: '',
      initialBalance: 0,
    });
  };


  const openEditModal = (supplier: SupplierWithBalance) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      email: supplier.email || '',
      phone: supplier.phone || '',
      company: supplier.company || '',
      notes: supplier.notes || '',
      bankName: supplier.bankName || '',
      accountName: supplier.accountName || '',
      accountNumber: supplier.accountNumber || '',
      sortCode: supplier.sortCode || '',
      routingNumber: supplier.routingNumber || '',
      iban: supplier.iban || '',
      swiftCode: supplier.swiftCode || '',
      bankCountry: supplier.bankCountry || '',
      accountType: supplier.accountType || '',
      paypalEmail: supplier.paypalEmail || '',
      initialBalance: 0,
    });
    setShowEditModal(true);
    setShowActionSheet(false);
  };


  const openDeleteAlert = (supplier: SupplierWithBalance) => {
    setSelectedSupplier(supplier);
    setShowDeleteAlert(true);
    setShowActionSheet(false);
  };

  const openTransactionModal = async (supplier: SupplierWithBalance) => {
    setSelectedSupplier(supplier);
    setShowActionSheet(false);
    setLoadingTransactions(true);
    setShowTransactionModal(true);
    
    try {
      const history = await supplierService.getTransactionHistory(supplier._id || supplier.id || '');
      setTransactionHistory(history);
    } catch (error: any) {
      console.error('Failed to load transaction history:', error);
      setToastMessage('Failed to load transaction history');
      setShowToast(true);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const openPaymentModal = async (supplier: SupplierWithBalance) => {
    setSelectedSupplier(supplier);
    setShowActionSheet(false);
    setLoadingPayments(true);
    setShowPaymentModal(true);
    
    try {
      const history = await supplierService.getPaymentHistory(supplier._id || supplier.id || '');
      setPaymentHistory(history);
    } catch (error: any) {
      console.error('Failed to load payment history:', error);
      setToastMessage('Failed to load payment history');
      setShowToast(true);
    } finally {
      setLoadingPayments(false);
    }
  };

  const openAddPaymentModal = async (supplier: SupplierWithBalance) => {
    setSelectedSupplier(supplier);
    setShowActionSheet(false);
    setLoadingUnpaidTransactions(true);
    setShowAddPaymentModal(true);
    setSelectedTransactions([]);
    
    // Reset form
    setPaymentFormData({
      amount: 0,
      type: 'sent',
      paymentMethod: 'bankTransfer',
      paymentDate: new Date().toISOString().split('T')[0],
      description: '',
      reference: '',
      notes: '',
    });
    
    try {
      const unpaid = await supplierService.getUnpaidTransactions(supplier._id || supplier.id || '');
      setUnpaidTransactions(unpaid);
    } catch (error: any) {
      console.error('Failed to load unpaid transactions:', error);
      setToastMessage('Failed to load unpaid transactions');
      setShowToast(true);
    } finally {
      setLoadingUnpaidTransactions(false);
    }
  };

  const handleTransactionSelection = (transactionId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTransactions([...selectedTransactions, transactionId]);
    } else {
      setSelectedTransactions(selectedTransactions.filter(id => id !== transactionId));
    }
  };

  const calculateSelectedAmount = () => {
    if (!unpaidTransactions?.transactions) return 0;
    return unpaidTransactions.transactions
      .filter((t: any) => selectedTransactions.includes(t.id))
      .reduce((sum: number, t: any) => sum + t.remainingAmount, 0);
  };

  const handleCreatePayment = async () => {
    if (!selectedSupplier) return;

    try {
      const linkedTransactions = unpaidTransactions?.transactions
        .filter((t: any) => selectedTransactions.includes(t.id))
        .map((t: any) => ({
          transactionId: t.id,
          transactionType: t.type,
          amount: t.remainingAmount, // Pay the full remaining amount
        }));

      const paymentData = {
        ...paymentFormData,
        linkedTransactions: linkedTransactions || [],
      };

      await supplierService.createPayment(selectedSupplier._id || selectedSupplier.id || '', paymentData);
      
      setToastMessage('Payment created successfully');
      setShowToast(true);
      setShowAddPaymentModal(false);
      
      // Refresh data
      await loadSuppliers();
    } catch (error: any) {
      console.error('Failed to create payment:', error);
      setToastMessage(error.response?.data?.message || 'Failed to create payment');
      setShowToast(true);
    }
  };

  const getTotalBalance = () => {
    return suppliers.reduce((sum, supplier) => sum + (supplier.calculatedBalance || 0), 0);
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'danger';  // Red for balances you owe (positive = you owe)
    if (balance < 0) return 'success'; // Green for balances owed to you (negative = they owe)
    return 'medium'; // Gray for zero balance
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton menu="main-menu" />
            </IonButtons>
            <IonTitle>Suppliers</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <CenteredLoader message="Loading suppliers..." />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton menu="main-menu" />
          </IonButtons>
          <IonTitle>Suppliers</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => setSearchText(e.detail.value!)}
            placeholder="Search suppliers"
            className="px-2"
          />
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        {/* Summary Card */}
        <div className="p-4">
          <IonCard className="shadow-lg">
            <IonCardHeader className="bg-primary-50 dark:bg-primary-900/30">
              <IonCardTitle className="text-lg">Summary</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonGrid className="p-0">
                <IonRow>
                  <IonCol size="6">
                    <div className="text-center">
                      <IonIcon icon={peopleOutline} className="text-3xl text-primary mb-2" />
                      <p className="text-2xl font-bold">{suppliers.length}</p>
                      <p className="text-sm text-gray-500">Total Suppliers</p>
                    </div>
                  </IonCol>
                  <IonCol size="6">
                    <div className="text-center">
                      <IonIcon icon={walletOutline} className="text-3xl text-primary mb-2" />
                      <p className="text-2xl font-bold">
                        {getCurrencySymbol(DEFAULT_CURRENCY)}
                        {Math.abs(getTotalBalance()).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {getTotalBalance() >= 0 ? 'You owe total' : 'They owe total'}
                      </p>
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </IonCard>
        </div>

        {/* Suppliers List */}
        <div className="px-4">
          {filteredSuppliers.length === 0 ? (
            <div className="text-center py-12">
              <IonIcon icon={businessOutline} className="text-6xl text-gray-300 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchText ? 'No suppliers found' : 'No suppliers yet'}
              </p>
            </div>
          ) : (
            <IonList className="space-y-2">
              {filteredSuppliers.map((supplier) => (
                <IonCard key={supplier._id || supplier.id} className="shadow-md">
                  <IonCardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          {supplier.name}
                          {supplier.company && (
                            <IonChip color="primary" className="text-xs">
                              {supplier.company}
                            </IonChip>
                          )}
                        </h3>
                        
                        <div className="mt-2 space-y-1">
                          {supplier.email && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                              <IonIcon icon={mailOutline} className="text-base" />
                              {supplier.email}
                            </p>
                          )}
                          {supplier.phone && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                              <IonIcon icon={callOutline} className="text-base" />
                              {supplier.phone}
                            </p>
                          )}
                          {(supplier.bankName || supplier.paypalEmail || supplier.accountType) && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {supplier.bankName && (
                                <IonChip color="tertiary" className="text-xs">
                                  <IonIcon icon={cashOutline} />
                                  Bank: {supplier.bankName}
                                </IonChip>
                              )}
                              {supplier.accountType && (
                                <IonChip color="primary" className="text-xs">
                                  <IonIcon icon={businessOutline} />
                                  {supplier.accountType === 'business' ? 'Business' : 'Private'}
                                </IonChip>
                              )}
                              {supplier.paypalEmail && (
                                <IonChip color="secondary" className="text-xs">
                                  PayPal
                                </IonChip>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="mt-3 flex items-center gap-4">
                          <IonBadge color={getBalanceColor(supplier.calculatedBalance || 0)}>
                            {supplier.calculatedBalance >= 0 ? 'You owe: ' : 'They owe: '}
                            {getCurrencySymbol(DEFAULT_CURRENCY)}
                            {Math.abs(supplier.calculatedBalance || 0).toFixed(2)}
                          </IonBadge>
                          
                          <span className="text-sm text-gray-500">
                            {supplier.salesCount || 0} sales
                          </span>

                        </div>


                        {/* Action Buttons */}
                        <div className="mt-4 flex flex-wrap gap-2">
                          <IonButton
                            size="small"
                            fill="outline"
                            color="primary"
                            onClick={() => openTransactionModal(supplier)}
                          >
                            <IonIcon icon={listOutline} slot="start" />
                            Transactions
                          </IonButton>
                          
                          <IonButton
                            size="small"
                            fill="outline"
                            color="secondary"
                            onClick={() => openPaymentModal(supplier)}
                          >
                            <IonIcon icon={cardOutline} slot="start" />
                            Payments
                          </IonButton>
                          
                          <IonButton
                            size="small"
                            fill="solid"
                            color="success"
                            onClick={() => openAddPaymentModal(supplier)}
                          >
                            <IonIcon icon={addCircleOutline} slot="start" />
                            New Payment
                          </IonButton>
                        </div>
                      </div>

                      <IonButton
                        fill="clear"
                        size="small"
                        onClick={() => {
                          setSelectedSupplier(supplier);
                          setShowActionSheet(true);
                        }}
                      >
                        <IonIcon icon={ellipsisVertical} />
                      </IonButton>
                    </div>
                  </IonCardContent>
                </IonCard>
              ))}
            </IonList>
          )}
        </div>

        {/* Add Button */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => setShowAddModal(true)}>
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </IonFab>

        {/* Add Modal */}
        <IonModal isOpen={showAddModal} onDidDismiss={() => setShowAddModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Add Supplier</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowAddModal(false)}>Cancel</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Name *</IonLabel>
                <IonInput
                  value={formData.name}
                  onIonChange={(e) => setFormData({ ...formData, name: e.detail.value! })}
                  placeholder="Supplier name"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Company</IonLabel>
                <IonInput
                  value={formData.company}
                  onIonChange={(e) => setFormData({ ...formData, company: e.detail.value! })}
                  placeholder="Company name"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Email</IonLabel>
                <IonInput
                  type="email"
                  value={formData.email}
                  onIonChange={(e) => setFormData({ ...formData, email: e.detail.value! })}
                  placeholder="email@example.com"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Phone</IonLabel>
                <IonInput
                  type="tel"
                  value={formData.phone}
                  onIonChange={(e) => setFormData({ ...formData, phone: e.detail.value! })}
                  placeholder="+1234567890"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Notes</IonLabel>
                <IonTextarea
                  value={formData.notes}
                  onIonChange={(e) => setFormData({ ...formData, notes: e.detail.value! })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </IonItem>

              <div className="ion-margin-top">
                <h4>Bank Details</h4>
                <IonItem>
                  <IonLabel position="stacked">Bank Name</IonLabel>
                  <IonInput
                    value={formData.bankName}
                    onIonChange={(e) => setFormData({ ...formData, bankName: e.detail.value! })}
                    placeholder="Enter bank name"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Account Name</IonLabel>
                  <IonInput
                    value={formData.accountName}
                    onIonChange={(e) => setFormData({ ...formData, accountName: e.detail.value! })}
                    placeholder="Enter account holder name"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Account Number</IonLabel>
                  <IonInput
                    value={formData.accountNumber}
                    onIonChange={(e) => setFormData({ ...formData, accountNumber: e.detail.value! })}
                    placeholder="Enter account number"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Sort Code</IonLabel>
                  <IonInput
                    value={formData.sortCode}
                    onIonChange={(e) => setFormData({ ...formData, sortCode: e.detail.value! })}
                    placeholder="00-00-00"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Routing Number</IonLabel>
                  <IonInput
                    value={formData.routingNumber}
                    onIonChange={(e) => setFormData({ ...formData, routingNumber: e.detail.value! })}
                    placeholder="Enter routing number"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">IBAN</IonLabel>
                  <IonInput
                    value={formData.iban}
                    onIonChange={(e) => setFormData({ ...formData, iban: e.detail.value! })}
                    placeholder="Enter IBAN"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">SWIFT Code</IonLabel>
                  <IonInput
                    value={formData.swiftCode}
                    onIonChange={(e) => setFormData({ ...formData, swiftCode: e.detail.value! })}
                    placeholder="Enter SWIFT code"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Bank Country</IonLabel>
                  <IonInput
                    value={formData.bankCountry}
                    onIonChange={(e) => setFormData({ ...formData, bankCountry: e.detail.value! })}
                    placeholder="e.g., United Kingdom"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Account Type</IonLabel>
                  <IonSelect
                    value={formData.accountType}
                    onIonChange={(e) => setFormData({ ...formData, accountType: e.detail.value })}
                    placeholder="Select account type"
                  >
                    <IonSelectOption value="private">Private</IonSelectOption>
                    <IonSelectOption value="business">Business</IonSelectOption>
                  </IonSelect>
                </IonItem>
              </div>

              <div className="ion-margin-top">
                <h4>PayPal Details</h4>
                <IonItem>
                  <IonLabel position="stacked">PayPal Email</IonLabel>
                  <IonInput
                    type="email"
                    value={formData.paypalEmail}
                    onIonChange={(e) => setFormData({ ...formData, paypalEmail: e.detail.value! })}
                    placeholder="paypal@example.com"
                  />
                </IonItem>
              </div>

              <div className="ion-margin-top">
                <h4>Initial Balance</h4>
                <IonItem>
                  <IonLabel position="stacked">Starting Balance</IonLabel>
                  <IonInput
                    type="number"
                    value={formData.initialBalance}
                    onIonChange={(e) => setFormData({ ...formData, initialBalance: parseFloat(e.detail.value!) || 0 })}
                    placeholder="0.00"
                  />
                </IonItem>
              </div>
            </IonList>
            <IonButton expand="block" onClick={handleAddSupplier} className="mt-4">
              Add Supplier
            </IonButton>
          </IonContent>
        </IonModal>

        {/* Edit Modal */}
        <IonModal isOpen={showEditModal} onDidDismiss={() => setShowEditModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Edit Supplier</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowEditModal(false)}>Cancel</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Name *</IonLabel>
                <IonInput
                  value={formData.name}
                  onIonChange={(e) => setFormData({ ...formData, name: e.detail.value! })}
                  placeholder="Supplier name"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Company</IonLabel>
                <IonInput
                  value={formData.company}
                  onIonChange={(e) => setFormData({ ...formData, company: e.detail.value! })}
                  placeholder="Company name"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Email</IonLabel>
                <IonInput
                  type="email"
                  value={formData.email}
                  onIonChange={(e) => setFormData({ ...formData, email: e.detail.value! })}
                  placeholder="email@example.com"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Phone</IonLabel>
                <IonInput
                  type="tel"
                  value={formData.phone}
                  onIonChange={(e) => setFormData({ ...formData, phone: e.detail.value! })}
                  placeholder="+1234567890"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Notes</IonLabel>
                <IonTextarea
                  value={formData.notes}
                  onIonChange={(e) => setFormData({ ...formData, notes: e.detail.value! })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </IonItem>

              <div className="ion-margin-top">
                <h4>Bank Details</h4>
                <IonItem>
                  <IonLabel position="stacked">Bank Name</IonLabel>
                  <IonInput
                    value={formData.bankName}
                    onIonChange={(e) => setFormData({ ...formData, bankName: e.detail.value! })}
                    placeholder="Enter bank name"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Account Name</IonLabel>
                  <IonInput
                    value={formData.accountName}
                    onIonChange={(e) => setFormData({ ...formData, accountName: e.detail.value! })}
                    placeholder="Enter account holder name"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Account Number</IonLabel>
                  <IonInput
                    value={formData.accountNumber}
                    onIonChange={(e) => setFormData({ ...formData, accountNumber: e.detail.value! })}
                    placeholder="Enter account number"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Sort Code</IonLabel>
                  <IonInput
                    value={formData.sortCode}
                    onIonChange={(e) => setFormData({ ...formData, sortCode: e.detail.value! })}
                    placeholder="00-00-00"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Routing Number</IonLabel>
                  <IonInput
                    value={formData.routingNumber}
                    onIonChange={(e) => setFormData({ ...formData, routingNumber: e.detail.value! })}
                    placeholder="Enter routing number"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">IBAN</IonLabel>
                  <IonInput
                    value={formData.iban}
                    onIonChange={(e) => setFormData({ ...formData, iban: e.detail.value! })}
                    placeholder="Enter IBAN"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">SWIFT Code</IonLabel>
                  <IonInput
                    value={formData.swiftCode}
                    onIonChange={(e) => setFormData({ ...formData, swiftCode: e.detail.value! })}
                    placeholder="Enter SWIFT code"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Bank Country</IonLabel>
                  <IonInput
                    value={formData.bankCountry}
                    onIonChange={(e) => setFormData({ ...formData, bankCountry: e.detail.value! })}
                    placeholder="e.g., United Kingdom"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Account Type</IonLabel>
                  <IonSelect
                    value={formData.accountType}
                    onIonChange={(e) => setFormData({ ...formData, accountType: e.detail.value })}
                    placeholder="Select account type"
                  >
                    <IonSelectOption value="private">Private</IonSelectOption>
                    <IonSelectOption value="business">Business</IonSelectOption>
                  </IonSelect>
                </IonItem>
              </div>

              <div className="ion-margin-top">
                <h4>PayPal Details</h4>
                <IonItem>
                  <IonLabel position="stacked">PayPal Email</IonLabel>
                  <IonInput
                    type="email"
                    value={formData.paypalEmail}
                    onIonChange={(e) => setFormData({ ...formData, paypalEmail: e.detail.value! })}
                    placeholder="paypal@example.com"
                  />
                </IonItem>
              </div>
            </IonList>
            <IonButton expand="block" onClick={handleEditSupplier} className="mt-4">
              Update Supplier
            </IonButton>
          </IonContent>
        </IonModal>


        {/* Add Payment Modal */}
        <IonModal isOpen={showAddPaymentModal} onDidDismiss={() => setShowAddPaymentModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Add Payment</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowAddPaymentModal(false)}>Cancel</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedSupplier && (
              <>
                <IonCard className="mb-4">
                  <IonCardHeader>
                    <IonCardTitle>{selectedSupplier.name}</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <p className="text-sm text-gray-600">
                      Create a payment record and optionally link it to unpaid transactions
                    </p>
                  </IonCardContent>
                </IonCard>

                {/* Payment Details */}
                <IonList>
                  <IonItem>
                    <IonLabel position="stacked">Payment Type *</IonLabel>
                    <IonSelect
                      value={paymentFormData.type}
                      onIonChange={(e) => setPaymentFormData({ ...paymentFormData, type: e.detail.value })}
                    >
                      <IonSelectOption value="sent">Payment Sent (to supplier)</IonSelectOption>
                      <IonSelectOption value="received">Payment Received (from supplier)</IonSelectOption>
                    </IonSelect>
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">Amount *</IonLabel>
                    <IonInput
                      type="number"
                      value={paymentFormData.amount}
                      onIonChange={(e) => setPaymentFormData({ 
                        ...paymentFormData, 
                        amount: parseFloat(e.detail.value!) || 0 
                      })}
                      placeholder="0.00"
                    />
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">Payment Method *</IonLabel>
                    <IonSelect
                      value={paymentFormData.paymentMethod}
                      onIonChange={(e) => setPaymentFormData({ ...paymentFormData, paymentMethod: e.detail.value })}
                    >
                      <IonSelectOption value="cash">Cash</IonSelectOption>
                      <IonSelectOption value="card">Card</IonSelectOption>
                      <IonSelectOption value="bankTransfer">Bank Transfer</IonSelectOption>
                      <IonSelectOption value="paypal">PayPal</IonSelectOption>
                      <IonSelectOption value="other">Other</IonSelectOption>
                    </IonSelect>
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">Payment Date *</IonLabel>
                    <IonInput
                      type="date"
                      value={paymentFormData.paymentDate}
                      onIonChange={(e) => setPaymentFormData({ ...paymentFormData, paymentDate: e.detail.value! })}
                    />
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">Description</IonLabel>
                    <IonInput
                      value={paymentFormData.description}
                      onIonChange={(e) => setPaymentFormData({ ...paymentFormData, description: e.detail.value! })}
                      placeholder="Payment description..."
                    />
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">Reference</IonLabel>
                    <IonInput
                      value={paymentFormData.reference}
                      onIonChange={(e) => setPaymentFormData({ ...paymentFormData, reference: e.detail.value! })}
                      placeholder="Transaction reference..."
                    />
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">Notes</IonLabel>
                    <IonTextarea
                      value={paymentFormData.notes}
                      onIonChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.detail.value! })}
                      placeholder="Additional notes..."
                      rows={3}
                    />
                  </IonItem>
                </IonList>

                {/* Unpaid Transactions Selection */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3">Link to Unpaid Transactions (Optional)</h3>
                  
                  {loadingUnpaidTransactions ? (
                    <div className="flex justify-center py-8">
                      <IonSpinner />
                    </div>
                  ) : unpaidTransactions?.transactions && unpaidTransactions.transactions.length > 0 ? (
                    <>
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          Select transactions this payment covers. The payment amount will be automatically calculated based on your selection.
                        </p>
                        {selectedTransactions.length > 0 && (
                          <p className="text-sm font-semibold text-blue-900 mt-2">
                            Selected Amount: {getCurrencySymbol(DEFAULT_CURRENCY)}{calculateSelectedAmount().toFixed(2)}
                          </p>
                        )}
                      </div>

                      <IonList>
                        {unpaidTransactions.transactions.map((transaction: any) => (
                          <IonItem key={transaction.id}>
                            <IonCheckbox
                              slot="start"
                              checked={selectedTransactions.includes(transaction.id)}
                              onIonChange={(e) => handleTransactionSelection(transaction.id, e.detail.checked)}
                            />
                            <IonLabel>
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-semibold">{transaction.eventName}</h4>
                                  <div className="flex items-center gap-2 mt-1">
                                    <IonChip 
                                      color={transaction.type === 'purchase' ? 'danger' : 'success'}
                                      className="text-xs"
                                    >
                                      {transaction.type === 'purchase' ? 'Purchase' : 'Sale'}
                                    </IonChip>
                                    <IonChip 
                                      color={transaction.paymentStatus === 'unpaid' ? 'warning' : 'medium'}
                                      className="text-xs"
                                    >
                                      {transaction.paymentStatus}
                                    </IonChip>
                                  </div>
                                  {transaction.section && (
                                    <p className="text-sm text-gray-600">
                                      {transaction.section}{transaction.row && `, Row: ${transaction.row}`}
                                      {transaction.seat && `, Seat: ${transaction.seat}`}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    {format(new Date(transaction.date), 'MMM dd, yyyy')}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold">
                                    {getCurrencySymbol(transaction.currency || DEFAULT_CURRENCY)}
                                    {transaction.remainingAmount.toFixed(2)}
                                  </p>
                                  <p className="text-xs text-gray-500">remaining</p>
                                  {transaction.paidAmount > 0 && (
                                    <p className="text-xs text-green-600">
                                      {getCurrencySymbol(transaction.currency || DEFAULT_CURRENCY)}
                                      {transaction.paidAmount.toFixed(2)} paid
                                    </p>
                                  )}
                                </div>
                              </div>
                            </IonLabel>
                          </IonItem>
                        ))}
                      </IonList>

                      {selectedTransactions.length > 0 && (
                        <IonButton
                          expand="block"
                          fill="outline"
                          onClick={() => {
                            const calculatedAmount = calculateSelectedAmount();
                            setPaymentFormData({ ...paymentFormData, amount: calculatedAmount });
                          }}
                          className="mt-4"
                        >
                          Use Selected Amount ({getCurrencySymbol(DEFAULT_CURRENCY)}{calculateSelectedAmount().toFixed(2)})
                        </IonButton>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <IonIcon icon={checkboxOutline} className="text-4xl text-gray-300 mb-2" />
                      <p className="text-gray-500">No unpaid transactions found</p>
                    </div>
                  )}
                </div>

                <IonButton 
                  expand="block" 
                  onClick={handleCreatePayment}
                  disabled={!paymentFormData.amount || paymentFormData.amount <= 0}
                  className="mt-6"
                >
                  Create Payment
                </IonButton>
              </>
            )}
          </IonContent>
        </IonModal>

        {/* Payment History Modal */}
        <IonModal isOpen={showPaymentModal} onDidDismiss={() => setShowPaymentModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Payment History</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowPaymentModal(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {loadingPayments ? (
              <div className="flex justify-center items-center py-12">
                <IonSpinner />
              </div>
            ) : paymentHistory ? (
              <>
                {/* Summary */}
                <IonCard className="mb-4">
                  <IonCardHeader>
                    <IonCardTitle className="text-lg">{paymentHistory.supplier?.name}</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonGrid className="p-0">
                      <IonRow>
                        <IonCol size="6" className="text-center">
                          <p className="text-lg font-bold text-red-600">
                            {getCurrencySymbol(DEFAULT_CURRENCY)}{paymentHistory.summary?.totalSent?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-sm text-gray-500">Sent</p>
                        </IonCol>
                        <IonCol size="6" className="text-center">
                          <p className="text-lg font-bold text-green-600">
                            {getCurrencySymbol(DEFAULT_CURRENCY)}{paymentHistory.summary?.totalReceived?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-sm text-gray-500">Received</p>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol size="6" className="text-center">
                          <p className="text-xl font-bold">
                            {getCurrencySymbol(DEFAULT_CURRENCY)}
                            {Math.abs(paymentHistory.summary?.netPayments || 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500">
                            Net {(paymentHistory.summary?.netPayments || 0) >= 0 ? 'Sent' : 'Received'}
                          </p>
                        </IonCol>
                        <IonCol size="6" className="text-center">
                          <p className="text-xl font-bold">
                            {paymentHistory.summary?.totalPayments || 0}
                          </p>
                          <p className="text-sm text-gray-500">Total Payments</p>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </IonCardContent>
                </IonCard>

                {/* Payments List */}
                {paymentHistory.payments && paymentHistory.payments.length > 0 ? (
                  <IonList>
                    {paymentHistory.payments.map((payment: any) => (
                      <IonCard key={payment._id} className="mb-3">
                        <IonCardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <IonChip 
                                  color={payment.type === 'sent' ? 'danger' : 'success'}
                                  className="text-xs"
                                >
                                  {payment.type === 'sent' ? 'Sent' : 'Received'}
                                </IonChip>
                                <IonChip 
                                  color={payment.status === 'completed' ? 'success' : payment.status === 'pending' ? 'warning' : 'medium'}
                                  className="text-xs"
                                >
                                  {payment.status}
                                </IonChip>
                                <span className="text-sm text-gray-500">
                                  {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                                </span>
                              </div>
                              
                              <h4 className="font-semibold">
                                {payment.description || `${payment.type === 'sent' ? 'Payment to' : 'Payment from'} ${paymentHistory.supplier?.name}`}
                              </h4>
                              
                              <div className="text-sm text-gray-600 mt-1">
                                <p>Method: {payment.paymentMethod}</p>
                                {payment.reference && <p>Reference: {payment.reference}</p>}
                                {payment.transactionId && <p>Transaction ID: {payment.transactionId}</p>}
                              </div>
                              
                              {payment.notes && (
                                <p className="text-sm text-gray-600 mt-2">{payment.notes}</p>
                              )}
                            </div>
                            
                            <div className="text-right">
                              <p className={`font-bold text-lg ${payment.type === 'sent' ? 'text-red-600' : 'text-green-600'}`}>
                                {payment.type === 'sent' ? '-' : '+'}
                                {getCurrencySymbol(payment.currency || DEFAULT_CURRENCY)}
                                {payment.amount.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </IonCardContent>
                      </IonCard>
                    ))}
                  </IonList>
                ) : (
                  <div className="text-center py-12">
                    <IonIcon icon={cardOutline} className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-500">No payments found</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Failed to load payment history</p>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Transaction History Modal */}
        <IonModal isOpen={showTransactionModal} onDidDismiss={() => setShowTransactionModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Transaction History</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowTransactionModal(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {loadingTransactions ? (
              <div className="flex justify-center items-center py-12">
                <IonSpinner />
              </div>
            ) : transactionHistory ? (
              <>
                {/* Summary */}
                <IonCard className="mb-4">
                  <IonCardHeader>
                    <IonCardTitle className="text-lg">{transactionHistory.supplier?.name}</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonGrid className="p-0">
                      <IonRow>
                        <IonCol size="6" className="text-center">
                          <p className="text-lg font-bold">{transactionHistory.summary?.totalPurchases || 0}</p>
                          <p className="text-sm text-gray-500">Purchases</p>
                        </IonCol>
                        <IonCol size="6" className="text-center">
                          <p className="text-lg font-bold">{transactionHistory.summary?.totalSales || 0}</p>
                          <p className="text-sm text-gray-500">Sales</p>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol size="12" className="text-center">
                          <p className="text-xl font-bold">
                            Balance: {getCurrencySymbol(DEFAULT_CURRENCY)}
                            {Math.abs(transactionHistory.summary?.calculatedBalance || 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {(transactionHistory.summary?.calculatedBalance || 0) >= 0 ? 'You owe' : 'They owe'}
                          </p>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </IonCardContent>
                </IonCard>

                {/* Transactions List */}
                {transactionHistory.transactions && transactionHistory.transactions.length > 0 ? (
                  <IonList>
                    {transactionHistory.transactions.map((transaction: any) => (
                      <IonCard key={transaction.id} className="mb-3">
                        <IonCardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <IonChip 
                                  color={transaction.type === 'purchase' ? 'danger' : 'success'}
                                  className="text-xs"
                                >
                                  {transaction.type === 'purchase' ? 'Purchase' : 'Sale'}
                                </IonChip>
                                <IonChip 
                                  color={
                                    transaction.paymentStatus === 'paid' ? 'success' : 
                                    transaction.paymentStatus === 'partially_paid' ? 'warning' : 'medium'
                                  }
                                  className="text-xs"
                                >
                                  {transaction.paymentStatus === 'paid' ? 'Paid' : 
                                   transaction.paymentStatus === 'partially_paid' ? 'Partial' : 'Unpaid'}
                                </IonChip>
                                <span className="text-sm text-gray-500">
                                  {format(new Date(transaction.date), 'MMM dd, yyyy')}
                                </span>
                              </div>
                              <h4 className="font-semibold">{transaction.eventName}</h4>
                              {transaction.section && (
                                <p className="text-sm text-gray-600">
                                  Section: {transaction.section}
                                  {transaction.row && `, Row: ${transaction.row}`}
                                  {transaction.seat && `, Seat: ${transaction.seat}`}
                                </p>
                              )}
                              {transaction.orderNumber && (
                                <p className="text-xs text-gray-500">Order: {transaction.orderNumber}</p>
                              )}
                              {transaction.buyerName && (
                                <p className="text-xs text-gray-500">Buyer: {transaction.buyerName}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold">
                                {getCurrencySymbol(transaction.currency || DEFAULT_CURRENCY)}
                                {transaction.totalAmount.toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-500">
                                Qty: {transaction.quantity}
                              </p>
                              {transaction.paidAmount > 0 && (
                                <p className="text-xs text-green-600">
                                  Paid: {getCurrencySymbol(transaction.currency || DEFAULT_CURRENCY)}
                                  {transaction.paidAmount.toFixed(2)}
                                </p>
                              )}
                              {transaction.balanceAfter !== undefined && (
                                <p className="text-xs text-gray-500">
                                  Balance: {getCurrencySymbol(transaction.currency || DEFAULT_CURRENCY)}
                                  {Math.abs(transaction.balanceAfter).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                          {transaction.notes && (
                            <p className="text-sm text-gray-600 mt-2">{transaction.notes}</p>
                          )}
                        </IonCardContent>
                      </IonCard>
                    ))}
                  </IonList>
                ) : (
                  <div className="text-center py-12">
                    <IonIcon icon={listOutline} className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-500">No transactions found</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Failed to load transaction history</p>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          buttons={[
            {
              text: 'Edit',
              icon: createOutline,
              handler: () => {
                if (selectedSupplier) openEditModal(selectedSupplier);
              },
            },
            {
              text: 'Delete',
              icon: trashOutline,
              role: 'destructive',
              handler: () => {
                if (selectedSupplier) openDeleteAlert(selectedSupplier);
              },
            },
            {
              text: 'Cancel',
              role: 'cancel',
            },
          ]}
        />

        {/* Delete Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Confirm Delete"
          message={`Are you sure you want to delete ${selectedSupplier?.name}?`}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Delete',
              handler: handleDeleteSupplier,
            },
          ]}
        />

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
        />
      </IonContent>
    </IonPage>
  );
};

export default Suppliers;